// Cơ chế "đang chạy không có người ngồi trước máy" (T0 của epic #247/#277).
//
// VÌ SAO CÓ FILE NÀY: extension VSCode spawn CLI với stdin đóng. Mọi lời gọi
// `@inquirer/prompts` trong đường đó treo vô hạn — tiến trình không chết, không
// báo gì. File này dựng MỘT cách duy nhất để phát hiện chế độ, và MỘT hành vi
// chuẩn khi gặp điểm hỏi mà không có giá trị: thoát ngay, báo lỗi máy đọc được.
//
// ⛔ BẤT BIẾN D1 (chốt 2026-08-15): `isTTY` — stdin lẫn stdout — KHÔNG BAO GIỜ
// bật chế độ này. Chỉ đúng 2 tín hiệu tường minh: cờ `--non-interactive` hoặc
// env `AVATAR_NON_INTERACTIVE=1`. Lý do: 6 chỗ đang dùng `stdout.isTTY` mang
// nghĩa "skip mềm / tắt màu"; nối chúng vào thoát cứng là đổi hành vi ngầm của
// mọi đường ống hiện có (pipe, CI log, `| tee`).

import type { Command } from "commander";
import { JSON_SCHEMA_VERSION } from "./machine-readable-json-output-contract.js";

/** Tên cờ dài, dùng chung cho mọi lệnh lá — không đẻ biến thể. */
export const NON_INTERACTIVE_FLAG = "--non-interactive";

/** Env tương đương cờ, cho môi trường không truyền được argv (hook, wrapper). */
export const NON_INTERACTIVE_ENV = "AVATAR_NON_INTERACTIVE";

const FLAG_DESCRIPTION =
  "Chạy không tương tác: không hỏi gì, thiếu giá trị bắt buộc thì thoát ≠0 kèm JSON 1 dòng ở stderr";

// Trạng thái module, do `preAction` hook của commander bơm vào trước khi bất kỳ
// `.action()` nào chạy. Dùng biến module thay vì truyền `opts` xuống ~63 điểm
// hỏi: đường truyền tay dài như thế chắc chắn rơi ở đâu đó, và AC2 không bắt được.
let flagSeenOnCommandChain = false;

// Cờ của lệnh đang chạy, gom từ chuỗi lệnh trong `preAction`.
//
// VÌ SAO CẦN: nhóm cờ recovery (`--on-failure`, `--pat-user`, `--reset-git`, …)
// được tiêu thụ ở các hàm nằm SÂU 4-5 tầng dưới `.action()` — luồn tham số qua
// từng tầng thì chắc chắn có tầng quên truyền, và lỗi đó im lặng (prompt lại hiện
// ra). Đọc từ một chỗ duy nhất thì không tầng nào quên được.
let activeCommandFlags: Record<string, unknown> = {};

/**
 * Chế độ non-interactive đang bật?
 *
 * Đọc env ở thời điểm gọi (không cache) để test set/unset được, và để wrapper
 * đặt env sau khi CLI đã khởi động vẫn có tác dụng.
 */
export function isNonInteractive(): boolean {
  return flagSeenOnCommandChain || process.env[NON_INTERACTIVE_ENV] === "1";
}

/**
 * Bật/tắt trạng thái từ chuỗi lệnh commander. Chỉ `registerNonInteractiveHook`
 * và test gọi hàm này.
 */
export function setNonInteractiveFromCommandChain(on: boolean): void {
  flagSeenOnCommandChain = on;
}

/** Trả trạng thái về mặc định. Test gọi trong `beforeEach`. */
export function resetNonInteractiveContext(): void {
  flagSeenOnCommandChain = false;
  activeCommandFlags = {};
}

/** Ghi cờ của lệnh đang chạy. Chỉ hook và test gọi. */
export function setActiveCommandFlags(flags: Record<string, unknown>): void {
  activeCommandFlags = flags;
}

/**
 * Đọc một cờ của lệnh đang chạy.
 *
 * Trả `undefined` khi không ở chế độ non-interactive: cờ chỉ được phép thay câu
 * hỏi khi máy gọi đã nói rõ "không có ai trả lời". Có TTY mà vẫn đọc cờ ở đây thì
 * ta lặng lẽ đổi hành vi đường tương tác — đúng thứ Nguyên tắc 4 cấm.
 */
export function getActiveFlag<T = string>(name: string): T | undefined {
  if (!isNonInteractive()) return undefined;
  const value = activeCommandFlags[name];
  return value === undefined || value === null || value === "" ? undefined : (value as T);
}

/**
 * Lỗi "chế độ non-interactive nhưng thiếu giá trị bắt buộc" (D5).
 *
 * Mang đủ 3 mẩu phía gọi cần để tự sửa: cờ nào thiếu, ở lệnh nào, và gợi ý điền.
 */
export class MissingFlagError extends Error {
  readonly missingFlag: string;
  readonly command: string;
  readonly hint: string;

  constructor(params: { missingFlag: string; command: string; hint: string }) {
    super(`Thiếu ${params.missingFlag} ở lệnh \`${params.command}\` (chế độ non-interactive)`);
    this.name = "MissingFlagError";
    this.missingFlag = params.missingFlag;
    this.command = params.command;
    this.hint = params.hint;
  }
}

/** Nhận diện lỗi qua `name` — `instanceof` gãy khi module bị nạp 2 lần (ESM + bundle). */
export function isMissingFlagError(err: unknown): err is MissingFlagError {
  return err instanceof Error && err.name === "MissingFlagError";
}

/**
 * In lỗi ra stderr đúng 1 dòng JSON (luật 4 của hợp đồng máy đọc) và trả mã thoát.
 *
 * Trả mã thay vì tự `process.exit`: caller quyết định lúc nào thoát, nhờ vậy
 * hàm này test được mà không giết tiến trình test.
 */
export function reportMissingFlag(err: MissingFlagError): number {
  process.stderr.write(
    `${JSON.stringify({
      schema_version: JSON_SCHEMA_VERSION,
      command: err.command,
      missing_flag: err.missingFlag,
      hint: err.hint,
    })}\n`,
  );
  return 1;
}

/**
 * Giá trị bắt buộc ở chế độ non-interactive: có thì trả, không có thì ném.
 *
 * Chuỗi rỗng coi như KHÔNG có — `--ai-provider ""` là lỗi người dùng, không phải
 * giá trị hợp lệ; để lọt xuống dưới thì lỗi nổ ở chỗ khác, khó truy.
 */
export function requireFlag<T>(
  value: T | undefined | null,
  spec: { flag: string; command: string; hint: string },
): T {
  if (value === undefined || value === null || value === "") {
    throw new MissingFlagError({
      missingFlag: spec.flag,
      command: spec.command,
      hint: spec.hint,
    });
  }
  return value;
}

/** Đăng ký cờ `--non-interactive` lên MỘT lệnh. */
export function attachNonInteractiveFlag(cmd: Command): Command {
  const already = cmd.options.some((o) => o.long === NON_INTERACTIVE_FLAG);
  if (!already) cmd.option(NON_INTERACTIVE_FLAG, FLAG_DESCRIPTION);
  return cmd;
}

/**
 * Đăng ký cờ lên `program` VÀ mọi lệnh con, đệ quy.
 *
 * ⚠️ Vì sao phải cả hai: cờ toàn cục của commander KHÔNG tự nhận sau tên lệnh con
 * (`avatar sync --non-interactive` → *unknown option* nếu chỉ khai ở `program`).
 * Duyệt cây thay vì gọi tay ở 31 chỗ: gọi tay thì quên một lệnh là im lặng, còn
 * duyệt cây thì lệnh mới thêm về sau tự có cờ.
 */
export function attachNonInteractiveFlagToAllCommands(root: Command): void {
  attachNonInteractiveFlag(root);
  for (const child of root.commands) attachNonInteractiveFlagToAllCommands(child);
}

/**
 * Bơm trạng thái trước khi `.action()` chạy: duyệt ngược chuỗi lệnh để bắt cờ
 * dù nó đứng trước (`avatar --non-interactive sync`) hay sau (`avatar sync --non-interactive`).
 */
export function registerNonInteractiveHook(root: Command): void {
  root.hook("preAction", (_thisCommand, actionCommand) => {
    // Gom cờ từ gốc xuống lá: lá ghi đè tổ tiên khi trùng tên, vì cờ đặt sát lệnh
    // là ý định cụ thể hơn.
    const chain: Command[] = [];
    let cursor: Command | null = actionCommand;
    while (cursor) {
      chain.unshift(cursor);
      cursor = cursor.parent;
    }

    let merged: Record<string, unknown> = {};
    let seen = false;
    for (const cmd of chain) {
      const opts = cmd.opts() as Record<string, unknown>;
      if (opts.nonInteractive === true) seen = true;
      merged = { ...merged, ...opts };
    }

    setNonInteractiveFromCommandChain(seen);
    setActiveCommandFlags(merged);
  });
}
